import { useEffect, useRef, type FocusEvent } from "react";

/**
 * Calls `onBlur` once focus has left both the picker root and its portaled
 * popup. React bubbles popup focus events through the portal to the root's
 * handler, so moving between trigger and popup never counts as a blur.
 */
export const useDatePickerBlur = (onBlur: (() => void) | undefined) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<number | undefined>(undefined);

    useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const isInsidePicker = (target: EventTarget | null) =>
        target instanceof Node &&
        [rootRef.current, popupRef.current].some(
            (element) => element?.contains(target) === true,
        );

    const handleBlur = (event: FocusEvent) => {
        if (!onBlur || isInsidePicker(event.relatedTarget)) {
            return;
        }
        // Base UI can bounce focus through focus guards or restore it to the
        // trigger after a blur with no relatedTarget; judge where it settles.
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
            if (!isInsidePicker(document.activeElement)) {
                onBlur();
            }
        });
    };

    return { rootRef, popupRef, handleBlur };
};
